import { z } from 'zod';
import { orgListReferenceSchema } from './contact-book';

/**
 * Hub automations (wayai#4931): a trigger starts work in this hub.
 *
 * One automation is a trigger, a target and an action. The vocabulary below is the program's
 * whole D2 shape, declared once so storage, these contracts and `hub.yaml` spell every variant
 * the same way. Which variants a hub can SAVE is narrower, and is decided in one place:
 * `validateAutomation` in `apps/backend/src/automations/validate.ts`, which refuses every variant
 * the fire path does not run, naming the automation. A config the platform cannot fire is
 * refused at save, never stored inert.
 */

export const AUTOMATION_TRIGGER_TYPES = ['schedule', 'event', 'webhook'] as const;
export const AUTOMATION_TARGET_TYPES = ['none', 'contact_list'] as const;
export const AUTOMATION_ACTION_TYPES = ['run_agent', 'send_message'] as const;
export const AUTOMATION_CHANNEL_TYPES = ['whatsapp', 'instagram', 'email', 'app'] as const;

export type AutomationTriggerType = typeof AUTOMATION_TRIGGER_TYPES[number];
export type AutomationTargetType = typeof AUTOMATION_TARGET_TYPES[number];
export type AutomationActionType = typeof AUTOMATION_ACTION_TYPES[number];
export type AutomationChannelType = typeof AUTOMATION_CHANNEL_TYPES[number];

/** The status an automation run records: `running` while it fires, then its outcome. */
export const AUTOMATION_RUN_STATUSES = ['running', 'completed', 'partial', 'failed'] as const;
export type AutomationRunStatus = typeof AUTOMATION_RUN_STATUSES[number];

/**
 * The action types a hub of `hubType` may save (wayai#4931 D3): a task hub's automations run an
 * agent; a chat hub's send a message or run an agent.
 */
export function automationActionTypesFor(hubType: string | undefined): AutomationActionType[] {
  return hubType === 'task' ? ['run_agent'] : ['send_message', 'run_agent'];
}

/** The channels each action fires on: `send_message` has no email or Instagram send path yet. */
export const AUTOMATION_ACTION_CHANNELS: Readonly<Record<AutomationActionType, readonly AutomationChannelType[]>> = {
  send_message: ['whatsapp', 'app'],
  run_agent: AUTOMATION_CHANNEL_TYPES,
};

/** The action fields the fire path may read. */
export const AUTOMATION_ACTION_FIELDS = ['connection_id', 'template_id', 'text', 'agent_id', 'instructions'] as const;
export type AutomationActionField = typeof AUTOMATION_ACTION_FIELDS[number];

/**
 * The fields an action reads on `channel` — every other field would be stored inert, so a save
 * refuses it. `send_message` sends a template on WhatsApp and a text elsewhere; `run_agent` opens
 * a conversation on a connection only on email.
 */
export function automationActionFields(
  type: AutomationActionType,
  channel: AutomationChannelType | undefined,
): ReadonlySet<AutomationActionField> {
  if (type === 'send_message') {
    return new Set(channel === 'whatsapp' ? ['connection_id', 'template_id'] : ['text']);
  }
  return new Set(channel === 'email' ? ['agent_id', 'instructions', 'connection_id'] : ['agent_id', 'instructions']);
}

/**
 * Size bounds. They make `listAutomations` a list with a stateable worst case on both axes
 * (AGENTS.md § Structure & Primitives, the 32 MiB rule): at most `AUTOMATIONS_PER_HUB_MAX` rows,
 * each bounded by these lengths.
 */
export const AUTOMATIONS_PER_HUB_MAX = 200;
export const AUTOMATION_NAME_MAX = 100;
export const AUTOMATION_DESCRIPTION_MAX = 1000;
export const AUTOMATION_INSTRUCTIONS_MAX = 10_000;
export const AUTOMATION_TEXT_MAX = 4096;
export const AUTOMATION_CRON_MAX = 512;

// ---------------------------------------------------------------------------
// Shape
// ---------------------------------------------------------------------------

/** When the automation fires. `schedule` carries a 5-field cron read in `timezone`. */
export const automationTriggerSchema = z.object({
  type: z.enum(AUTOMATION_TRIGGER_TYPES),
  cron: z.string().trim().min(1).max(AUTOMATION_CRON_MAX).optional(),
  timezone: z.string().trim().min(1).optional(),
}).strict();

/**
 * Who each fire is for. `contact_list` names one of the org's contact lists by name (`org_list`,
 * `contact-book.ts`); each fire reaches the list's members its hub sees, and a fire that finds no
 * list of that name records `list_not_found`.
 */
export const automationTargetSchema = z.object({
  type: z.enum(AUTOMATION_TARGET_TYPES),
  org_list: orgListReferenceSchema.optional(),
}).strict();

/**
 * What each fire does, per target. `send_message` sends a WhatsApp template (`template_id`) or a
 * text (`text`) on `channel`; `run_agent` hands `instructions` to the hub's agent on `channel`.
 */
export const automationActionSchema = z.object({
  type: z.enum(AUTOMATION_ACTION_TYPES),
  channel: z.enum(AUTOMATION_CHANNEL_TYPES).optional(),
  connection_id: z.string().uuid().nullable().optional(),
  template_id: z.string().uuid().nullable().optional(),
  text: z.string().max(AUTOMATION_TEXT_MAX).nullable().optional(),
  agent_id: z.string().uuid().nullable().optional(),
  instructions: z.string().max(AUTOMATION_INSTRUCTIONS_MAX).nullable().optional(),
}).strict();

export type AutomationTrigger = z.infer<typeof automationTriggerSchema>;
export type AutomationTarget = z.infer<typeof automationTargetSchema>;
export type AutomationAction = z.infer<typeof automationActionSchema>;

const automationName = z.string().trim().min(1).max(AUTOMATION_NAME_MAX);
const automationDescription = z.string().max(AUTOMATION_DESCRIPTION_MAX).nullable().optional();

// ---------------------------------------------------------------------------
// Params & Queries
// ---------------------------------------------------------------------------

export const automationIdParam = z.object({
  id: z.string().uuid(),
});

export const automationHubQuery = z.object({
  hub_id: z.string().uuid(),
});

/** Most runs one page of an automation's history carries. */
export const AUTOMATION_RUNS_PAGE_MAX = 50;

export const listAutomationRunsQuery = z.object({
  hub_id: z.string().uuid(),
  page: z.coerce.number().int().min(1).default(1),
  per_page: z.coerce.number().int().min(1).max(AUTOMATION_RUNS_PAGE_MAX).default(10),
});

// ---------------------------------------------------------------------------
// Bodies
// ---------------------------------------------------------------------------

/**
 * Create body. An automation is always created disabled; `POST /:id/toggle` enables it. Whether
 * an automation fires is derived, never set: it fires while it is enabled, not paused and valid
 * (wayai#4931 D15), and every write re-derives it.
 * Unknown keys are refused rather than dropped, so a field the platform does not run yet (a
 * gate, say) is never accepted and silently ignored.
 */
export const createAutomationBody = z.object({
  hub_id: z.string().uuid(),
  name: automationName,
  description: automationDescription,
  trigger: automationTriggerSchema,
  target: automationTargetSchema,
  action: automationActionSchema,
}).strict();

/**
 * Update body (the hub rides the `hub_id` query, like every route on one automation). Each of
 * `trigger`, `target` and `action` is replaced whole when present.
 */
export const updateAutomationBody = z.object({
  name: automationName.optional(),
  description: automationDescription,
  trigger: automationTriggerSchema.optional(),
  target: automationTargetSchema.optional(),
  action: automationActionSchema.optional(),
}).strict();

export type CreateAutomationBody = z.infer<typeof createAutomationBody>;
export type UpdateAutomationBody = z.infer<typeof updateAutomationBody>;

// ---------------------------------------------------------------------------
// Response types
// ---------------------------------------------------------------------------

export interface Automation {
  automation_id: string;
  hub_id: string;
  name: string;
  description: string | null;
  enabled: boolean;
  trigger: AutomationTrigger;
  target: AutomationTarget;
  action: AutomationAction;
  /**
   * Runtime state, kept apart from the automation's config (it never branches). `next_run_at` is
   * set exactly while a fire is scheduled.
   */
  last_run_at: string | null;
  next_run_at: string | null;
  total_runs: number;
  /** Set while the automation is paused — the stop a production hub's admin holds (D15). */
  paused_at: string | null;
  /** The user id of whoever paused it. */
  paused_by: string | null;
  /**
   * The last time the automation, enabled and not paused, could not fire, and why — a reference
   * gone (an agent, a template, a connection) or disabled. Cleared by the next fire that runs.
   */
  last_skip_reason: string | null;
  last_skipped_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ListAutomationsResponse {
  automations: Automation[];
}

/** A connection a preview automation uses whose credential is also production's (D9). */
export interface SharedProductionConnection {
  connection_id: string;
  connection_display_name: string | null;
}

export interface GetAutomationResponse {
  automation: Automation;
  /**
   * On a preview hub: the connections the automation acts through — its own and its agent's,
   * tools included — whose credential is also production's, so a preview fire reaches the
   * systems production reaches. Empty on a production hub.
   */
  shared_production_connections: SharedProductionConnection[];
}

export interface CreateAutomationResponse {
  automation_id: string;
}

export interface ToggleAutomationResponse {
  ok: true;
  enabled: boolean;
  next_run_at: string | null;
}

export interface RunAutomationNowResponse {
  ok: true;
  run_id: string;
}

/** `POST /:id/pause` and `POST /:id/resume`. */
export interface PauseAutomationResponse {
  ok: true;
  paused_at: string | null;
  next_run_at: string | null;
}

/**
 * Why a fire reached no target, as its run records it: `list_not_found` (the org list it names
 * does not exist) and `list_over_cap` (the list holds more contacts the hub sees than
 * `platform_config.automation_list_max_contacts`), each of which also raises the hub's alert; and
 * `list_unavailable` (the contact book or the cap could not be read; the next fire reads again).
 */
export const AUTOMATION_RUN_ERROR_CODES = ['list_not_found', 'list_over_cap', 'list_unavailable'] as const;
export type AutomationRunErrorCode = typeof AUTOMATION_RUN_ERROR_CODES[number];

/**
 * The hub Status & Notices alert category each run error that raises one raises (a `warn` alert of
 * no connection, `HubAlert.category`).
 */
export const AUTOMATION_ALERT_CATEGORIES = {
  list_not_found: 'automation_list_not_found',
  list_over_cap: 'automation_list_over_cap',
} as const satisfies Partial<Record<AutomationRunErrorCode, string>>;
export type AutomationAlertCategory = typeof AUTOMATION_ALERT_CATEGORIES[keyof typeof AUTOMATION_ALERT_CATEGORIES];

/**
 * The category of the alert an automation that is enabled and not paused but cannot fire raises
 * (wayai#4931 D15): one active per automation (`HubAlert` of no connection), its `detail` the reason
 * naming the automation. It clears once the automation fires, or is paused, disabled or fixed.
 */
export const AUTOMATION_BLOCKED_ALERT_CATEGORY = 'automation_blocked';

export interface AutomationRun {
  run_id: string;
  automation_id: string;
  status: AutomationRunStatus;
  /** Why the fire reached no target, or null. */
  error_code: AutomationRunErrorCode | null;
  total_targets: number;
  succeeded: number;
  failed: number;
  skipped: number;
  started_at: string;
  completed_at: string | null;
}

export interface ListAutomationRunsResponse {
  runs: AutomationRun[];
  total: number;
  page: number;
  per_page: number;
}
